import { Autocomplete } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useDebouncedCallback } from 'use-debounce';

import PaperWithLoader, { LoadingMoreContext } from './PaperWithLoader';

export interface SearchPage<T> {
  items: T[];
  total: number;
}

interface OptionGroup<T> {
  key: string;
  label: string;
  items: T[];
}

interface InfiniteSearchBarProps<T extends { id: string }> {
  fetchPage: (searchText: string, page: number) => Promise<SearchPage<T>>;
  getGroupKey: (item: T) => string;
  getLabel: (item: T) => string;
  onSelect: (items: T[]) => void;
  onSearchTextChange?: (searchText: string) => void;
  placeholder?: string;
}

const SCROLL_THRESHOLD_PX = 40;

function InfiniteSearchBar<T extends { id: string }>({
  fetchPage,
  getGroupKey,
  getLabel,
  onSelect,
  onSearchTextChange,
  placeholder = 'Search...'
}: InfiniteSearchBarProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<OptionGroup<T> | null>(null);
  const [items, setItems] = useState<T[]>([]);
  const [loadingPage, setLoadingPage] = useState<number | null>(null);

  // Refs so rapid scroll events and stale responses see the latest values without waiting for a render.
  const requestId = useRef(0);
  const inFlight = useRef(false);
  const pageState = useRef({ text: '', page: 0, fetched: 0, hasMore: false });

  const options = useMemo(() => {
    const groups = new Map<string, OptionGroup<T>>();
    const seenIds = new Set<string>();
    for (const item of items) {
      if (seenIds.has(item.id)) continue;
      seenIds.add(item.id);
      const key = getGroupKey(item);
      const group = groups.get(key);
      if (group) {
        group.items.push(item);
      } else {
        groups.set(key, { key, label: getLabel(item), items: [item] });
      }
    }
    return [...groups.values()];
  }, [items, getGroupKey, getLabel]);

  const loadPage = async (text: string, page: number) => {
    const id = ++requestId.current;
    inFlight.current = true;
    setLoadingPage(page);
    try {
      const result = await fetchPage(text, page);
      if (id !== requestId.current) return;
      const fetched = (page === 0 ? 0 : pageState.current.fetched) + result.items.length;
      pageState.current = { text, page, fetched, hasMore: result.items.length > 0 && fetched < result.total };
      setItems((prev) => (page === 0 ? result.items : [...prev, ...result.items]));
    } catch (error) {
      if (id === requestId.current) toast.error(`Error while searching: ${(error as Error).message}`);
    } finally {
      if (id === requestId.current) {
        inFlight.current = false;
        setLoadingPage(null);
      }
    }
  };

  const search = useDebouncedCallback((text: string) => {
    onSearchTextChange?.(text);
    if (text) {
      void loadPage(text, 0);
    } else {
      requestId.current++;
      inFlight.current = false;
      pageState.current = { text: '', page: 0, fetched: 0, hasMore: false };
      setItems([]);
      setLoadingPage(null);
    }
  }, 600);

  const handleListboxScroll = (event: React.UIEvent<HTMLElement>) => {
    const listbox = event.currentTarget;
    const { text, page, hasMore } = pageState.current;
    if (inFlight.current || !hasMore) return;
    if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - SCROLL_THRESHOLD_PX) {
      void loadPage(text, page + 1);
    }
  };

  return (
    <LoadingMoreContext.Provider value={loadingPage !== null && loadingPage > 0}>
      <Autocomplete
        id="infinite-search-id"
        value={selected}
        inputValue={searchTerm}
        loading={loadingPage === 0}
        options={searchTerm ? options : []}
        filterOptions={(groups) => groups}
        getOptionLabel={(group) => group.label}
        isOptionEqualToValue={(option, value) => option.key === value.key}
        onInputChange={(event, value) => {
          if (event?.type === 'change') {
            // A stale selection makes MUI reset the list scroll when pages are appended.
            setSelected(null);
            setSearchTerm(value);
            search(value);
          }
        }}
        onChange={(_, group) => {
          setSelected(group);
          setSearchTerm(group?.label ?? '');
          onSelect(group?.items ?? []);
        }}
        slots={{ paper: PaperWithLoader }}
        slotProps={{ listbox: { onScroll: handleListboxScroll } }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                style: {
                  fontSize: '17px',
                  fontWeight: 400,
                  borderRadius: '8px'
                }
              },
              htmlInput: {
                ...params.slotProps.htmlInput,
                className: '',
                style: {
                  fontSize: '17px',
                  fontWeight: 400
                }
              }
            }}
          />
        )}
        renderOption={({ key: _key, ...props }, group) => (
          <li key={group.key} {...props}>
            {group.label}
          </li>
        )}
      />
    </LoadingMoreContext.Provider>
  );
}

export default InfiniteSearchBar;
